<template>
  <div class="branch-dte-preview" :style="themeVariables">
    <component :is="'style'">{{ activeStyles }}</component>

    <div class="branch-dte-preview__header">
      <div>
        <h3 class="branch-dte-preview__title">Vista previa en vivo</h3>
        <p class="branch-dte-preview__subtitle">
          {{ layoutLabel }} · {{ branchName }}
        </p>
      </div>
    </div>

    <div class="branch-dte-preview__pages">
      <print-pos-dte-letter-standard-layout
        v-if="renderLayoutKey === 'standard'"
        :document-data="documentData"
        :pages="pages"
      />

      <print-pos-dte-letter-full-layout
        v-else-if="renderLayoutKey === 'full'"
        :document-data="documentData"
        :pages="pages"
      />

      <print-pos-dte-letter-compact-layout
        v-else
        :document-data="documentData"
        :pages="pages"
      />
    </div>
  </div>
</template>

<script>
import PrintPosDteLetterCompactLayout from "@/components/ReportsAndPrintings/DteLetter/layouts/PrintPosDteLetterCompactLayout.vue";
import PrintPosDteLetterFullLayout from "@/components/ReportsAndPrintings/DteLetter/layouts/PrintPosDteLetterFullLayout.vue";
import PrintPosDteLetterStandardLayout from "@/components/ReportsAndPrintings/DteLetter/layouts/PrintPosDteLetterStandardLayout.vue";
import commonStyles from "@/components/ReportsAndPrintings/DteLetter/styles/commonStyles";
import compactLayoutStyles from "@/components/ReportsAndPrintings/DteLetter/styles/compactLayoutStyles";
import fullLayoutStyles from "@/components/ReportsAndPrintings/DteLetter/styles/fullLayoutStyles";
import standardLayoutStyles from "@/components/ReportsAndPrintings/DteLetter/styles/standardLayoutStyles";
import { normalizeDtePrintData } from "@/components/ReportsAndPrintings/DteLetter/utils/dtePrintNormalizer";
import { paginateDteDocument } from "@/components/ReportsAndPrintings/DteLetter/utils/dtePrintPagination";
import { buildBranchDteTemplatePreviewData } from "@/helpers/dteTemplatePreviewData";
import {
  getDteTemplateLayoutByKey,
  mapDteTemplateLayoutToRenderKey,
  mapDteTemplateSettingsToTheme,
  safeResolveDteTemplateSettings,
} from "@/helpers/dteTemplateSettings";

export default {
  name: "BranchDteTemplatePreview",
  components: {
    PrintPosDteLetterCompactLayout,
    PrintPosDteLetterFullLayout,
    PrintPosDteLetterStandardLayout,
  },
  props: {
    branch: {
      type: Object,
      default: () => ({}),
    },
    business: {
      type: Object,
      default: () => ({}),
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    layoutKey: {
      type: String,
      default: "estandar",
    },
    previewAssets: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    activeStyles() {
      return [
        commonStyles,
        fullLayoutStyles,
        compactLayoutStyles,
        standardLayoutStyles,
      ].join("\n");
    },
    layoutLabel() {
      return getDteTemplateLayoutByKey(this.layoutKey).label;
    },
    branchName() {
      return (
        this.branch.BranchName ||
        this.branch.BranchLegalName ||
        "Sucursal sin nombre"
      );
    },
    renderLayoutKey() {
      return mapDteTemplateLayoutToRenderKey(this.layoutKey);
    },
    resolvedSettings() {
      return safeResolveDteTemplateSettings(this.settings, this.layoutKey);
    },
    theme() {
      return mapDteTemplateSettingsToTheme(this.resolvedSettings, {}, this.layoutKey);
    },
    themeVariables() {
      return {
        "--dte-brand": this.theme.brand,
        "--dte-brand-contrast": this.theme.brandContrast,
        "--dte-accent": this.theme.accent || "#d9a441",
        "--dte-surface": this.theme.surface,
        "--dte-border": this.theme.border,
        "--dte-table-border": this.theme.tableBorderColor,
        "--dte-card-radius": `${this.theme.cardBorderRadiusPx}px`,
        "--dte-table-radius": `${this.theme.tableBorderRadiusPx}px`,
        "--dte-text": this.theme.text || "#1b2a3a",
        "--dte-muted": this.theme.muted || "#6c7b8a",
        "--dte-card-header-gradient-strong": this.theme.cardHeaderGradientStrong,
        "--dte-card-header-gradient-soft": this.theme.cardHeaderGradientSoft,
        "--dte-title-font-size": `${this.resolvedSettings.theme.titleFontSizePx}px`,
        "--dte-text-font-size": `${this.resolvedSettings.theme.textFontSizePx}px`,
        "--dte-detail-font-size": `${this.resolvedSettings.theme.detailFontSizePx}px`,
      };
    },
    previewData() {
      return buildBranchDteTemplatePreviewData({
        branch: this.branch,
        business: this.business,
        previewAssets: this.previewAssets,
        showLineGuide: this.showLineGuide,
        hideRelatedDocuments: this.hideRelatedDocumentsPreview,
      });
    },
    documentData() {
      return normalizeDtePrintData(
        {
          show: true,
          data: this.previewData,
        },
        {
          hideQr: true,
          theme: this.theme,
          templateSettings: this.resolvedSettings,
          templateLayoutKey: this.layoutKey,
        }
      );
    },
    pages() {
      return paginateDteDocument(this.documentData, this.renderLayoutKey);
    },
    showLineGuide() {
      return Boolean(
        this.resolvedSettings.pagination &&
          this.resolvedSettings.pagination.showLineGuide
      );
    },
    hideRelatedDocumentsPreview() {
      return Boolean(
        this.resolvedSettings.pagination &&
          this.resolvedSettings.pagination.hideRelatedDocumentsPreview
      );
    },
  },
};
</script>

<style scoped>
.branch-dte-preview {
  min-height: 100%;
  background:
    radial-gradient(circle at top right, rgba(47, 93, 140, 0.14), transparent 28%),
    linear-gradient(180deg, #eef3f9 0%, #dde5ef 100%);
  padding: 20px;
}

.branch-dte-preview__header {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(10px);
}

.branch-dte-preview__title {
  margin: 0;
  font-size: 1rem;
  color: #23384d;
}

.branch-dte-preview__subtitle {
  margin: 4px 0 0;
  color: #5e7388;
  font-size: 0.85rem;
}

.branch-dte-preview__pages {
  display: grid;
  gap: 20px;
  justify-content: center;
}
</style>
